export const CALLABLE_ATTEMPT_TIMEOUT_MESSAGE = "ensure-timeout";

/**
 * Waits up to `timeoutMs` for the first successful result among `call` and any
 * earlier calls still in `inFlight`. A call that outlives its own timeout stays
 * in the pool, so a slow response that lands during a retry still wins instead
 * of being discarded. Rejects with the last error only once every call in the
 * pool has failed, or with `CALLABLE_ATTEMPT_TIMEOUT_MESSAGE` on timeout.
 */
export function awaitFirstCallableResult<T>(
    inFlight: Set<Promise<T>>,
    call: Promise<T>,
    timeoutMs: number,
): Promise<T> {
    inFlight.add(call);
    return new Promise<T>((resolve, reject) => {
        let settled = false;
        const timeoutId = setTimeout(() => {
            if (settled) return;
            settled = true;
            reject(new Error(CALLABLE_ATTEMPT_TIMEOUT_MESSAGE));
        }, timeoutMs);

        for (const pending of Array.from(inFlight)) {
            pending.then(
                (result) => {
                    if (settled) return;
                    settled = true;
                    clearTimeout(timeoutId);
                    resolve(result);
                },
                (error) => {
                    inFlight.delete(pending);
                    if (settled || inFlight.size > 0) return;
                    settled = true;
                    clearTimeout(timeoutId);
                    reject(error);
                },
            );
        }
    });
}
